// Session cookie helpers shared by client and server.
//
// The auth token is obtained client-side from the Nostr remote-signer handshake,
// so the cookie is written from the browser. It is intentionally NOT HttpOnly:
// the same value is read both here (client) and by SvelteKit server `load`
// functions via `cookies.get()` — the browser keeps sending it with navigations.
//
// This module is pure (no runes, no module-level state) so it is safe to import
// from server code. Reactive client state lives in `sessionStore.svelte.ts`.

import { browser } from '$app/environment';
import type { User } from '$lib/api/adminAPI';
import type { LoginType } from '$lib/nostr/types';

export const SESSION_COOKIE = 'tsunami_session';

export type Session = {
  token: string;
  pubkey: string;
  loginType: LoginType;
  // Unix timestamp (seconds) at which the auth token expires.
  expiresAt: number;
  // The authenticated user's admin-API record, fetched at login. Optional
  // because it may be unavailable (e.g. the user lacks `manage_admin_users`).
  currentUser?: User | null;
};

export const serializeSession = (session: Session): string =>
  encodeURIComponent(JSON.stringify(session));

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

// Parse a cookie value into a Session. Tolerates both raw and percent-encoded
// input because SvelteKit's `cookies.get()` already URL-decodes on the server
// while `document.cookie` returns the stored (encoded) value on the client.
export const parseSession = (raw: string | undefined | null): Session | null => {
  if (!raw) return null;

  for (const candidate of [raw, safeDecode(raw)]) {
    if (!candidate) continue;
    try {
      const decoded = JSON.parse(candidate) as Partial<Session>;
      if (
        typeof decoded.token === 'string' &&
        typeof decoded.pubkey === 'string' &&
        typeof decoded.loginType === 'string' &&
        typeof decoded.expiresAt === 'number'
      ) {
        return decoded as Session;
      }
    } catch {
      // try the next candidate
    }
  }

  return null;
};

// A session is valid while its token has not yet expired.
export const isSessionValid = (session: Session | null): session is Session =>
  !!session && session.expiresAt * 1000 > Date.now();

// Seconds until the token expires (never negative).
export const secondsUntilExpiry = (session: Session): number =>
  Math.max(0, session.expiresAt - Math.floor(Date.now() / 1000));

// --- Client-side cookie access (document.cookie) ---------------------------

export const readSessionCookie = (): Session | null => {
  if (!browser) return null;

  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${SESSION_COOKIE}=`));

  if (!entry) return null;

  return parseSession(entry.slice(SESSION_COOKIE.length + 1));
};

export const writeSessionCookie = (session: Session) => {
  if (!browser) return;

  const maxAge = secondsUntilExpiry(session);
  document.cookie = `${SESSION_COOKIE}=${serializeSession(session)}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

export const deleteSessionCookie = () => {
  if (!browser) return;

  document.cookie = `${SESSION_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};
